import { z } from 'zod';
import { localizedText, shortCode, uuid } from './schemas.ts';
import { RESTRICTION_AREAS, type RestrictionArea } from './partners.ts';

/**
 * Documentos e homologação documental dos parceiros (C3a.3, ADR 0017): entradas da API e o cálculo
 * da situação documental, usados pela API, pelas telas e pela aptidão para contratar.
 */

export const DOCUMENT_SENSITIVITIES = ['normal', 'restricted'] as const;
export type DocumentSensitivity = (typeof DOCUMENT_SENSITIVITIES)[number];

export const DOCUMENT_STATUSES = ['pending_review', 'approved', 'rejected', 'superseded'] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

/** Situação de um tipo de documento para o parceiro numa empresa. */
export type DocumentSituation =
  'missing' | 'requested' | 'in_review' | 'rejected' | 'valid' | 'expiring' | 'expired';

/** Documento válido que vence em até tantos dias aparece como "vencendo". */
export const DOCUMENT_EXPIRING_DAYS = 30;

// ----- Entradas -----

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const partnerCategoryInput = z.object({
  code: shortCode,
  name: localizedText,
  isActive: z.boolean().default(true),
});

export const documentTypeInput = z.object({
  code: shortCode,
  name: localizedText,
  description: z
    .object({ 'pt-BR': z.string().trim().max(500), en: z.string().trim().max(500) })
    .default({ 'pt-BR': '', en: '' }),
  /** Vazio = vale para o grupo inteiro. */
  legalEntityId: uuid.nullable().default(null),
  /** Vazio = todas as categorias. */
  categoryId: uuid.nullable().default(null),
  requiresExpiry: z.boolean().default(false),
  requiredForApproval: z.boolean().default(false),
  responsibleArea: z.enum(RESTRICTION_AREAS),
  sensitivity: z.enum(DOCUMENT_SENSITIVITIES).default('normal'),
  isActive: z.boolean().default(true),
});
export type DocumentTypeInput = z.infer<typeof documentTypeInput>;

export const documentRequestInput = z.object({
  documentTypeId: uuid,
  legalEntityId: uuid.nullable().default(null),
  dueDate: isoDate.nullable().optional(),
  note: optionalText(1000),
});

/** Campos do formulário de envio (vêm antes do arquivo, no mesmo envio). */
export const documentUploadFields = z
  .object({
    documentTypeId: uuid,
    legalEntityId: uuid.nullable().default(null),
    requestId: uuid.nullable().default(null),
    issuedOn: isoDate.nullable().default(null),
    expiresOn: isoDate.nullable().default(null),
  })
  .refine((v) => !v.issuedOn || !v.expiresOn || v.expiresOn >= v.issuedOn, {
    path: ['expiresOn'],
    message: 'A validade não pode ser antes da emissão',
  });

/** Aprovar ou rejeitar: sempre com justificativa. */
export const documentReviewInput = z.object({ comment: z.string().trim().min(1).max(1000) });
export const documentRequestCancelInput = z.object({
  reason: z.string().trim().min(1).max(1000),
});

// ----- Situação documental -----

export interface SituationDocumentType {
  id: string;
  legalEntityId: string | null;
  categoryId: string | null;
  requiredForApproval: boolean;
  requiresExpiry: boolean;
  responsibleArea: RestrictionArea;
  isActive: boolean;
}

export interface SituationDocument {
  id: string;
  documentTypeId: string;
  legalEntityId: string | null;
  status: DocumentStatus;
  expiresOn: string | null;
  createdAt: Date | string;
}

export interface SituationRequest {
  documentTypeId: string;
  legalEntityId: string | null;
  status: 'open' | 'fulfilled' | 'cancelled';
  dueDate: string | null;
}

/** Tipos que valem para o parceiro na empresa: do grupo ou da empresa, da categoria dele ou de todas. */
export function applicableDocumentTypes<T extends SituationDocumentType>(
  types: readonly T[],
  legalEntityId: string,
  categoryId: string | null,
): T[] {
  return types.filter(
    (t) =>
      t.isActive &&
      (t.legalEntityId == null || t.legalEntityId === legalEntityId) &&
      (t.categoryId == null || t.categoryId === categoryId),
  );
}

function addDays(isoDay: string, days: number): string {
  const d = new Date(`${isoDay}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Situação de cada tipo exigido para o parceiro na empresa, e se ele está regular (todos os
 * obrigatórios válidos, mesmo que vencendo). `today` no formato AAAA-MM-DD.
 */
export function documentSituation(input: {
  types: readonly SituationDocumentType[];
  documents: readonly SituationDocument[];
  requests: readonly SituationRequest[];
  legalEntityId: string;
  categoryId: string | null;
  today: string;
}): {
  regular: boolean;
  items: {
    documentTypeId: string;
    required: boolean;
    situation: DocumentSituation;
    currentDocumentId: string | null;
    expiresOn: string | null;
  }[];
} {
  const inScope = (v: { legalEntityId: string | null }) =>
    v.legalEntityId == null || v.legalEntityId === input.legalEntityId;
  const expiringLimit = addDays(input.today, DOCUMENT_EXPIRING_DAYS);
  const items = applicableDocumentTypes(input.types, input.legalEntityId, input.categoryId).map(
    (t) => {
      const docs = input.documents
        .filter((d) => d.documentTypeId === t.id && inScope(d))
        .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
      const approved = docs.filter((d) => d.status === 'approved');
      const valid = approved.find((d) => !d.expiresOn || d.expiresOn >= input.today);
      let situation: DocumentSituation;
      let current = valid ?? null;
      if (valid) {
        situation = valid.expiresOn && valid.expiresOn <= expiringLimit ? 'expiring' : 'valid';
      } else if (docs.some((d) => d.status === 'pending_review')) {
        situation = 'in_review';
        current = docs.find((d) => d.status === 'pending_review') ?? null;
      } else if (approved.length > 0) {
        situation = 'expired';
        current = approved[0]!;
      } else if (docs[0]?.status === 'rejected') {
        situation = 'rejected';
        current = docs[0];
      } else if (
        input.requests.some((r) => r.documentTypeId === t.id && r.status === 'open' && inScope(r))
      ) {
        situation = 'requested';
      } else {
        situation = 'missing';
      }
      return {
        documentTypeId: t.id,
        required: t.requiredForApproval,
        situation,
        currentDocumentId: current?.id ?? null,
        expiresOn: current?.expiresOn ?? null,
      };
    },
  );
  const regular = items
    .filter((i) => i.required)
    .every((i) => i.situation === 'valid' || i.situation === 'expiring');
  return { regular, items };
}
